import React, { useState } from 'react';
import { useAttendance } from '../context/AttendanceContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, HeadingLevel, AlignmentType, WidthType } from 'docx';

const { FiBarChart3, FiUsers, FiCalendar, FiTrendingUp, FiTrendingDown, FiExternalLink, FiFileText, FiChevronDown, FiChevronRight } = FiIcons;

const Analytics = () => {
  const { getAnalytics, setCurrentDate } = useAttendance();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedStudents, setExpandedStudents] = useState({});
  
  const analytics = getAnalytics();
  const { studentStats, dateStats, totalStudents, totalDays } = analytics;

  const goToDate = (date) => {
    setCurrentDate(date);
    navigate('/');
  };

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), 'MMM d, yyyy');
  };

  const getAttendanceColor = (rate) => {
    if (rate >= 90) return 'text-green-600 bg-green-100';
    if (rate >= 75) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const toggleStudentExpansion = (studentId) => {
    setExpandedStudents(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const exportWordReport = async () => {
    const currentDate = new Date();
    const reportDate = format(currentDate, 'MMMM d, yyyy');
    const fileName = `attendance-report-${format(currentDate, 'yyyy-MM-dd-HHmm')}.docx`;

    // Calculate overall statistics
    const totalPossibleAttendance = totalStudents * totalDays;
    const totalPresent = studentStats.reduce((sum, student) => sum + student.attendedDates.length, 0);
    const overallAttendanceRate = totalPossibleAttendance > 0 ? (totalPresent / totalPossibleAttendance) * 100 : 0;

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            // Title
            new Paragraph({
              children: [
                new TextRun({
                  text: "ATTENDANCE REPORT",
                  bold: true,
                  size: 32,
                }),
              ],
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
            }),
            
            new Paragraph({
              children: [
                new TextRun({
                  text: `Generated on ${reportDate}`,
                  italics: true,
                  size: 24,
                }),
              ],
              alignment: AlignmentType.CENTER,
            }),

            new Paragraph({ text: "" }), // Empty line

            // Summary Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "SUMMARY",
                  bold: true,
                  size: 28,
                }),
              ],
              heading: HeadingLevel.HEADING_1,
            }),

            new Paragraph({
              children: [
                new TextRun({ text: `Total Students: ${totalStudents}` }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({ text: `Total Days Tracked: ${totalDays}` }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({ text: `Overall Attendance Rate: ${overallAttendanceRate.toFixed(1)}%` }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({ 
                  text: `Average Daily Attendance: ${
                    dateStats.length > 0
                      ? Math.round(dateStats.reduce((sum, day) => sum + day.attendanceRate, 0) / dateStats.length)
                      : 0
                  }%` 
                }),
              ],
            }),

            new Paragraph({ text: "" }), // Empty line

            // Student Details Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "STUDENT ATTENDANCE DETAILS",
                  bold: true,
                  size: 28,
                }),
              ],
              heading: HeadingLevel.HEADING_1,
            }),

            // Student Table
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              rows: [
                // Header row
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Student Name", bold: true })] })],
                      width: { size: 25, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Attendance Rate", bold: true })] })],
                      width: { size: 15, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Days Present", bold: true })] })],
                      width: { size: 15, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Days Absent", bold: true })] })],
                      width: { size: 15, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Total Days", bold: true })] })],
                      width: { size: 15, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Dates Attended", bold: true })] })],
                      width: { size: 15, type: WidthType.PERCENTAGE },
                    }),
                  ],
                }),
                // Student rows
                ...studentStats
                  .sort((a, b) => b.attendanceRate - a.attendanceRate)
                  .map(student => 
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: student.name })] })],
                        }),
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: `${student.attendanceRate.toFixed(1)}%` })] })],
                        }),
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: student.attendedDates.length.toString() })] })],
                        }),
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: student.missedDates.length.toString() })] })],
                        }),
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: (student.attendedDates.length + student.missedDates.length).toString() })] })],
                        }),
                        new TableCell({
                          children: [new Paragraph({ 
                            children: [new TextRun({ 
                              text: student.attendedDates.length > 0 
                                ? student.attendedDates.sort().map(date => format(new Date(date), 'MM/dd/yyyy')).join(', ')
                                : 'None',
                              size: 20
                            })] 
                          })],
                        }),
                      ],
                    })
                  ),
              ],
            }),

            new Paragraph({ text: "" }), // Empty line

            // Daily Records Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "DAILY ATTENDANCE RECORDS",
                  bold: true,
                  size: 28,
                }),
              ],
              heading: HeadingLevel.HEADING_1,
            }),

            // Daily Records Table
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              rows: [
                // Header row
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Date", bold: true })] })],
                      width: { size: 30, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Present", bold: true })] })],
                      width: { size: 20, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Absent", bold: true })] })],
                      width: { size: 20, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Total", bold: true })] })],
                      width: { size: 15, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Rate", bold: true })] })],
                      width: { size: 15, type: WidthType.PERCENTAGE },
                    }),
                  ],
                }),
                // Daily records rows
                ...dateStats
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map(day => 
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: format(new Date(day.date), 'MM/dd/yyyy') })] })],
                        }),
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: day.present.toString() })] })],
                        }),
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: day.absent.toString() })] })],
                        }),
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: day.total.toString() })] })],
                        }),
                        new TableCell({
                          children: [new Paragraph({ children: [new TextRun({ text: `${day.attendanceRate.toFixed(1)}%` })] })],
                        }),
                      ],
                    })
                  ),
              ],
            }),

            new Paragraph({ text: "" }), // Empty line

            // Footer
            new Paragraph({
              children: [
                new TextRun({
                  text: "This report was generated automatically by the Attendance Tracker system.",
                  italics: true,
                  size: 20,
                }),
              ],
              alignment: AlignmentType.CENTER,
            }),
          ],
        },
      ],
    });

    // Generate and download the document
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <SafeIcon icon={FiBarChart3} className="w-8 h-8 mr-3 text-indigo-600" />
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 mt-2">Comprehensive attendance insights and statistics</p>
          </div>
          
          <button
            onClick={exportWordReport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <SafeIcon icon={FiFileText} className="w-5 h-5" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <SafeIcon icon={FiUsers} className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Students</p>
              <p className="text-2xl font-bold text-gray-900">{totalStudents}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <SafeIcon icon={FiCalendar} className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Days Tracked</p>
              <p className="text-2xl font-bold text-gray-900">{totalDays}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-indigo-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Avg Attendance</p>
              <p className="text-2xl font-bold text-gray-900">
                {dateStats.length > 0
                  ? Math.round(dateStats.reduce((sum, day) => sum + day.attendanceRate, 0) / dateStats.length)
                  : 0}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <SafeIcon icon={FiBarChart3} className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Best Day</p>
              <p className="text-2xl font-bold text-gray-900">
                {dateStats.length > 0
                  ? Math.max(...dateStats.map(d => d.attendanceRate)).toFixed(0)
                  : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('students')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'students'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Student Details
            </button>
            <button
              onClick={() => setActiveTab('dates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dates'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Daily Records
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Performers */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 mr-2 text-green-600" />
              Top Performers
            </h3>
            <div className="space-y-3">
              {studentStats
                .sort((a, b) => b.attendanceRate - a.attendanceRate)
                .slice(0, 5)
                .map((student) => (
                  <div key={student.id} className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{student.name}</span>
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${getAttendanceColor(student.attendanceRate)}`}>
                      {student.attendanceRate.toFixed(1)}%
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Recent Attendance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 text-indigo-600" />
              Recent Attendance
            </h3>
            <div className="space-y-3">
              {dateStats
                .slice(-5)
                .reverse()
                .map((day) => (
                  <div key={day.date} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{formatDate(day.date)}</p>
                      <p className="text-sm text-gray-500">{day.present}/{day.total} present</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${getAttendanceColor(day.attendanceRate)}`}>
                        {day.attendanceRate.toFixed(1)}%
                      </span>
                      <button
                        onClick={() => goToDate(day.date)}
                        className="text-indigo-600 hover:text-indigo-700"
                      >
                        <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'students' && (
        <div className="space-y-4">
          {studentStats
            .sort((a, b) => b.attendanceRate - a.attendanceRate)
            .map((student) => (
              <div key={student.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div 
                  className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleStudentExpansion(student.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <SafeIcon 
                        icon={expandedStudents[student.id] ? FiChevronDown : FiChevronRight} 
                        className="w-5 h-5 text-gray-500" 
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{student.name}</h3>
                        <p className="text-sm text-gray-500">
                          {student.attendedDates.length} present, {student.missedDates.length} absent
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getAttendanceColor(student.attendanceRate)}`}>
                        {student.attendanceRate.toFixed(1)}%
                      </span>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {student.attendedDates.length + student.missedDates.length} total days
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {expandedStudents[student.id] && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      {/* Days Present */}
                      <div>
                        <h4 className="text-sm font-semibold text-green-700 mb-3 flex items-center">
                          <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                          Days Present ({student.attendedDates.length})
                        </h4>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {student.attendedDates.length > 0 ? (
                            student.attendedDates
                              .sort()
                              .map((date) => (
                                <div key={`present-${date}`} className="flex items-center justify-between py-1 px-2 bg-green-50 rounded text-sm">
                                  <span className="text-green-800">{formatDate(date)}</span>
                                  <button
                                    onClick={() => goToDate(date)}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                                  </button>
                                </div>
                              ))
                          ) : (
                            <p className="text-gray-500 text-sm italic">No days present</p>
                          )}
                        </div>
                      </div>

                      {/* Days Absent */}
                      <div>
                        <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center">
                          <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                          Days Absent ({student.missedDates.length})
                        </h4>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {student.missedDates.length > 0 ? (
                            student.missedDates
                              .sort()
                              .map((date) => (
                                <div key={`absent-${date}`} className="flex items-center justify-between py-1 px-2 bg-red-50 rounded text-sm">
                                  <span className="text-red-800">{formatDate(date)}</span>
                                  <button
                                    onClick={() => goToDate(date)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                                  </button>
                                </div>
                              ))
                          ) : (
                            <p className="text-gray-500 text-sm italic">No days absent</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}

      {activeTab === 'dates' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Daily Attendance Records</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Attendance Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Present
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Absent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dateStats
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((day) => (
                    <tr key={day.date} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{formatDate(day.date)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAttendanceColor(day.attendanceRate)}`}>
                          {day.attendanceRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {day.present}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {day.absent}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {day.total}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => goToDate(day.date)}
                          className="text-indigo-600 hover:text-indigo-700 font-medium text-sm"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalStudents === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No data available. Add students and start tracking attendance to see analytics.</p>
        </div>
      )}
    </div>
  );
};

export default Analytics;